import { CkeditorConstants } from "./ckeditorconstants";
import { CKEditor5 } from "../ckeditor";

const {
  BLOCK_ELEMENTS,
  htmlElement: { HTML_SPAN, SPAN },
  htmlAttributes: { REL, CLASS, LIST_STYLE },
  linksTo,
  helperAttributes: { LINK_HREF, NUMBERED_CITATION, SUBTEMPLATE_NAME, LIST },
  class: { REF, WK_KEYWORD_REF },
  elementClass: { COMMENT }
} = CkeditorConstants;
export const subtemplateUtils = (editor, eventData) => {
  const {
    model: {
      document: { selection },
    },
    editing: { mapper },
  } = editor;

  const firstPos = selection.getFirstPosition();
  const posParent = firstPos.parent;
  const mappedViewEle = mapper.toViewElement(posParent);

  /*returns true if clicked item is link*/
  return (
    (
      mappedViewEle?.hasAttribute(REL) &&
      mappedViewEle?.getAttribute(REL).includes(linksTo)) ||
    (firstPos?.textNode?.hasAttribute(LINK_HREF))
  );
};

/**
 * To check citation is clicked or it's whole text citation selected
 */
export const checkIsCitation = (editor, eventData) => {
  const {
    model: {
      document: { selection },
    },
    editing: { mapper },
  } = editor;
  let isCitation = false;
  for (const value of selection.getRanges()) {
    for (const itemInSelection of value.getWalker()) {
      if (itemInSelection.item.name === NUMBERED_CITATION) {
        isCitation = true;
      }
    }
  }
  return isCitation;
};

export const checkSelectedLink = (editor, eventData) => {
  const {
    model: {
      document: { selection },
    },
    editing: { mapper },
  } = editor;
  let isSelectedLink;
  for (const value of selection.getRanges()) {
    for (const itemInSelection of value.getWalker()) {
      if (
        itemInSelection.item?.data &&
        (itemInSelection.item?.textNode?.parent.getAttribute(CLASS) === REF ||
          itemInSelection.item?.textNode?.hasAttribute(LINK_HREF))
      ) {
        isSelectedLink =
          itemInSelection.item.data === itemInSelection.item.textNode.data; // to check whole link is selected or not
      }
    }
  }
  /*return values are true, false and undefined -> click at the start or end of the link*/
  return isSelectedLink;
};

/**
 * Check selection in document includes a keyword
 * @param { CKEditor5 } editor
 * @returns { boolean }
 */
export const checkSelectedKeyword = (editor) => {
  const { model: { document: { selection } } } = editor;
  for (const value of selection.getRanges()) {
    for (const itemInSelection of value.getWalker()) {
      if (
        itemInSelection.item?.data &&
        itemInSelection.item?.textNode?.parent.getAttribute(CLASS) === WK_KEYWORD_REF
      ) {
        /*return true -> click in between the keyword*/
        return true;
      }
    }
  }
  /*return false -> click at the start or end of the keyword*/
  return false;
};

/*
 * Remove default htmlSpan attributes from citation markup model
 */
export const removeDefaultHtmlSpanAttributes = (modelItem, editor) => {
  modelItem.hasAttribute(HTML_SPAN) && modelItem._removeAttribute(HTML_SPAN);
  for (const child of [...modelItem.getChildren()]) {
    if (child.hasAttribute(HTML_SPAN) && child.is("$text")) {
      child._removeAttribute(HTML_SPAN);
    }
  }
  // Restricts creation of list over elements created via custom schema
  const listKeys = [];
  try {
    for (const key of modelItem.getAttributeKeys()) {
      if (key.includes(LIST)) {
        listKeys.push(key);
      }
    }
  } catch (err) {
    throw new Error(`Failed to get attribute keys from modelItem: ${err}`);
  }

  // If no list attributes, skipping writer logic completely
  if (listKeys.length === 0) {
    return;
  }

  // If list attributes present, removing using writer
  editor.model.change((writer) => {
    for (const key of listKeys) {
      try {
        writer.removeAttribute(key, modelItem);
      } catch (err) {
        throw new Error(`Failed to remove attributes "${key}": ${err}`);
      }
    }
  });
};

/**
 * Enable / Disable keyword plugin dropdown option based upon selection in document
 * @param { CKEditor5 } editor
 * @param { DocumentSelection } selection
 * @returns { boolean }
 */
export const enableDisableKeywordOption = (editor) => {
  const {
    model: {
      document: { selection },
    }
  } = editor;
  const isLinkClicked = subtemplateUtils(editor, null);
  const isCitationClicked = checkIsCitation(editor, null);
  const clickedElementPosition = selection.getFirstPosition();
  const isWholeLinkSelected = checkSelectedLink(editor, null);
  const isWholeKeywordSelected = checkSelectedKeyword(editor);

  let isDisabled = false;
  const element = editor.model.getSelectedContent(selection);
  let blockEle = [];
  const { _children: { _nodes } } = element;
  if (_nodes?.length > 0) {
    blockEle = _nodes.filter(ele => BLOCK_ELEMENTS.includes(ele.name));
  }

  if (blockEle.length > 0) {
    isDisabled = true;
  }

  if ((isLinkClicked && !clickedElementPosition.isAtStart && !clickedElementPosition.isAtEnd)
    || isCitationClicked ||
    isWholeLinkSelected != undefined ||
    isWholeKeywordSelected) {
    isDisabled = true
  }
  return isDisabled;
}

export const checkIsInlineElement = (element) => {
  const {
    body: { firstChild },
  } = new DOMParser().parseFromString(element, "text/html");
  return inlineElements?.includes(firstChild.tagName);
};

/**
 * Get selected content in document
 * @param { CKEditor5 } editor
 * @returns { string }
 */
export const getSelectedContent = (editor) => {
  return editor.data.stringify(
    editor.model.getSelectedContent(editor.model.document.selection)
  );
}

export const stringToBoolean = (str) => {
  return str === 'true' ? true : false
}

/**
 * Check the element is subtemplate or not
 * @param { CKEditor5 } editor
 * @returns { boolean }
 */
export const checkForSubtemplate = (editor) => {
  const elementClicked = editor.model.document.selection.getFirstPosition().parent;
  const toViewEle = editor.editing.mapper.toViewElement(elementClicked);
  const getAncestors = toViewEle.getAncestors();
  const isSubtemplate = getAncestors.filter((item) =>
    item.hasAttribute(SUBTEMPLATE_NAME)
  );

  if (!isSubtemplate.length) {
    for (const child of toViewEle.getChildren()) {
      if (child.name === SPAN && child.hasAttribute(SUBTEMPLATE_NAME)) {
        isSubtemplate.push(toViewEle);
        break;
      }
    }
  }
  return isSubtemplate.length ? true : false;
}

/**
 * Check selection in document includes a comments
 * @returns { boolean }
 */
export const checkSelectedComments = (editor) => {
  const { anchor, focus } = editor.editing.view.document.selection;
  try {
    return focus.getAncestors().reverse().find(ele => ele._classes?.has(COMMENT)) || anchor.getAncestors().reverse().find(ele => ele._classes?.has(COMMENT));
  } catch (err) {
    return false;
  }
};
